// Pitch Form Builder - Premium full-page editor
import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp,
  Sparkles, Zap, Type, AlignLeft, List, Upload, Tag, X, Pencil
} from 'lucide-react';
import { FIELD_TYPES } from './appConstants';
import { toast } from 'sonner';

// Field type config - NO URL type allowed
const FIELD_CONFIGS = [
  { type: FIELD_TYPES.TEXT, icon: Type, label: 'Short Text', desc: 'Single line input' },
  { type: FIELD_TYPES.TEXTAREA, icon: AlignLeft, label: 'Long Text', desc: 'Multi-line input' },
  { type: FIELD_TYPES.DROPDOWN, icon: List, label: 'Dropdown', desc: 'Select from options' },
  { type: FIELD_TYPES.FILE, icon: Upload, label: 'File Upload', desc: 'PDF, documents' },
  { type: FIELD_TYPES.LABEL, icon: Tag, label: 'Section Label', desc: 'Organize form' },
];

export default function PitchFormBuilder({ app, pageId, onSave, onClose }) {
  const [config, setConfig] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddFieldMenu, setShowAddFieldMenu] = useState(false);
  const addFieldRef = useRef(null);

  useEffect(() => {
    if (app) {
      const cleanedFields = (app.fields || []).filter(f => f.type !== 'url' && f.type !== FIELD_TYPES.URL);
      
      setConfig({
        ...app,
        fields: cleanedFields,
        button_text: app.button_text || 'Pitch to me',
        fund_summary: app.fund_summary || '',
        fund_points: app.fund_points || [],
        ai_sorting_prompt: app.ai_sorting_prompt || '',
        filter_by_industry: app.filter_by_industry !== false,
        filter_by_stage: app.filter_by_stage !== false,
      });
    }
  }, [app]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addFieldRef.current && !addFieldRef.current.contains(event.target)) {
        setShowAddFieldMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!config) return null;

  const addField = (type) => {
    const typeInfo = FIELD_CONFIGS.find(o => o.type === type);
    const newField = {
      id: `field_${crypto.randomUUID()}`,
      type,
      label: typeInfo?.label || 'New Field',
      placeholder: '',
      required: false,
      options: type === FIELD_TYPES.DROPDOWN ? ['Option 1', 'Option 2'] : []
    };
    setConfig(prev => ({ ...prev, fields: [...prev.fields, newField] }));
    setSelectedField(newField.id);
    setShowAddFieldMenu(false);
  };

  const updateField = (fieldId, updates) => {
    setConfig(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (fieldId) => {
    setConfig(prev => ({ ...prev, fields: prev.fields.filter(f => f.id !== fieldId) }));
    if (selectedField === fieldId) setSelectedField(null);
  };

  const moveField = (fieldId, direction) => {
    const fields = [...config.fields];
    const idx = fields.findIndex(f => f.id === fieldId);
    if (direction === 'up' && idx > 0) {
      [fields[idx], fields[idx - 1]] = [fields[idx - 1], fields[idx]];
    } else if (direction === 'down' && idx < fields.length - 1) {
      [fields[idx], fields[idx + 1]] = [fields[idx + 1], fields[idx]];
    }
    setConfig(prev => ({ ...prev, fields }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...config }, false); // false = close after save
      toast.success('Form saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleAcceptPitches = async () => {
    const newEnabled = !config.enabled;
    setConfig(prev => ({ ...prev, enabled: newEnabled }));
    try {
      await onSave({ ...config, enabled: newEnabled }, true); // true = keep form open
      toast.success(newEnabled ? 'Now accepting pitches!' : 'Pitches paused');
    } catch (err) {
      toast.error('Failed to update');
      setConfig(prev => ({ ...prev, enabled: !newEnabled }));
    }
  };

  const selectedFieldData = config.fields.find(f => f.id === selectedField);

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F8F9FB] flex flex-col">
      {/* Header */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-base font-semibold text-gray-900">Pitch Form Builder</h1>
        </div>
        
        {/* Accept Pitches Toggle - Center */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">Accept Pitches</span>
          <button
            onClick={toggleAcceptPitches}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              config.enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                config.enabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            config.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {config.enabled ? 'ON' : 'OFF'}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
            {saving ? 'Saving...' : 'Save Form'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - AI Settings Only (Wider) */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-auto">
          {/* AI Sorting Section - Expanded */}
          <div className="p-6 flex-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-lg">AI Sorting</h2>
                <p className="text-sm text-gray-500">How to prioritize incoming pitches</p>
              </div>
            </div>
            
            <div className="space-y-5">
              {/* Main Prompt - Larger and Clearer */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Sorting Criteria
                </label>
                <Textarea
                  value={config.ai_sorting_prompt}
                  onChange={(e) => setConfig(prev => ({ ...prev, ai_sorting_prompt: e.target.value }))}
                  placeholder="Describe how you want pitches prioritized...

Example:
• Prioritize founders with domain expertise
• Early traction (revenue, users) is a plus
• Technical founders preferred
• Deprioritize: consumer apps, hardware-only"
                  className="text-sm min-h-[180px] resize-none bg-gray-50 border-gray-200 focus:bg-white focus:border-amber-400 transition-colors"
                />
                <p className="text-xs text-gray-400 mt-2">
                  AI will score each pitch based on these criteria
                </p>
              </div>
              
              {/* Filter Options */}
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <h3 className="text-sm font-semibold text-amber-900 mb-3">Auto-Filter Options</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.filter_by_industry}
                      onChange={(e) => setConfig(prev => ({ ...prev, filter_by_industry: e.target.checked }))}
                      className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-amber-900">Match my industries</span>
                      <p className="text-xs text-amber-700">Prioritize startups in sectors I invest in</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.filter_by_stage}
                      onChange={(e) => setConfig(prev => ({ ...prev, filter_by_stage: e.target.checked }))}
                      className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-amber-900">Match my stage</span>
                      <p className="text-xs text-amber-700">Prioritize startups at stages I target</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Button Text - Bottom */}
          <div className="p-6 border-t border-gray-100 bg-gray-50">
            <label className="text-sm font-medium text-gray-700 mb-2 block">Submit Button Text</label>
            <Input
              value={config.button_text}
              onChange={(e) => setConfig(prev => ({ ...prev, button_text: e.target.value }))}
              placeholder="Pitch to me"
              className="h-10 text-sm bg-white"
            />
          </div>
        </div>

        {/* Center - Form Preview */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-xl mx-auto py-8 px-6">
            {/* How it works - Above Form */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-violet-900">How it works</span>
              </div>
              <div className="flex items-center gap-8 ml-1">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                  <span className="text-sm text-violet-700">Build your pitch form</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                  <span className="text-sm text-violet-700">AI auto-ranks pitches</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-violet-200 text-violet-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                  <span className="text-sm text-violet-700">Review in inbox</span>
                </div>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Form Header with Add Fields Button */}
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Submit Your Pitch</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Preview of how founders will see your form</p>
                </div>
                
                {/* Add Fields Dropdown Button */}
                <div className="relative" ref={addFieldRef}>
                  <button
                    onClick={() => setShowAddFieldMenu(!showAddFieldMenu)}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Field
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAddFieldMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showAddFieldMenu && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                      {FIELD_CONFIGS.map(({ type, icon: Icon, label, desc }) => (
                        <button
                          key={type}
                          onClick={() => addField(type)}
                          className="w-full px-4 py-3 hover:bg-violet-50 transition-colors text-left flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-800">{label}</div>
                            <div className="text-xs text-gray-400">{desc}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="p-5">
                {config.fields.length === 0 ? (
                  <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                    <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                      <Plus className="w-7 h-7 text-violet-500" />
                    </div>
                    <p className="text-gray-600 font-medium">No fields yet</p>
                    <p className="text-sm text-gray-400 mt-1">Click &quot;+ Add Field&quot; above to start building your form</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {config.fields.map((field, idx) => {
                      const typeConfig = FIELD_CONFIGS.find(c => c.type === field.type);
                      const Icon = typeConfig?.icon || Type;
                      const isSelected = selectedField === field.id;
                      
                      return (
                        <div
                          key={field.id}
                          className={`relative p-4 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-violet-400 bg-violet-50 shadow-sm' 
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          {/* Field Header with Actions */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-700">
                                {field.label || 'Untitled'}
                              </span>
                              {field.required && <span className="text-red-400 text-xs font-medium">Required</span>}
                            </div>
                            
                            {/* Action Buttons - Always Visible */}
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setSelectedField(isSelected ? null : field.id)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  isSelected ? 'bg-violet-200 text-violet-700' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                                }`}
                                title="Edit field"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveField(field.id, 'up')}
                                disabled={idx === 0}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move up"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => moveField(field.id, 'down')}
                                disabled={idx === config.fields.length - 1}
                                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                title="Move down"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => removeField(field.id)}
                                className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"
                                title="Delete field"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Field Preview */}
                          {field.type === FIELD_TYPES.TEXTAREA ? (
                            <div className="h-20 bg-gray-100 rounded-lg border border-gray-200 px-3 py-2">
                              <span className="text-sm text-gray-400">{field.placeholder || 'Enter text...'}</span>
                            </div>
                          ) : field.type === FIELD_TYPES.DROPDOWN ? (
                            <div className="space-y-1">
                              <div className="h-10 bg-gray-100 rounded-lg border border-gray-200 flex items-center px-3">
                                <span className="text-sm text-gray-400 flex-1">{field.placeholder || 'Select an option...'}</span>
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </div>
                              {field.multiSelect && (
                                <span className="text-xs text-violet-500 font-medium">Multiple selections allowed</span>
                              )}
                            </div>
                          ) : field.type === FIELD_TYPES.FILE ? (
                            <div className="h-14 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center gap-2">
                              <Upload className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-400">Click to upload file</span>
                            </div>
                          ) : field.type === FIELD_TYPES.LABEL ? (
                            <div className="border-t border-gray-200 pt-1" />
                          ) : (
                            <div className="h-10 bg-gray-100 rounded-lg border border-gray-200 px-3 flex items-center">
                              <span className="text-sm text-gray-400">{field.placeholder || 'Enter text...'}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Submit Button Preview */}
                {config.fields.length > 0 && (
                  <button disabled className="w-full mt-5 py-3 bg-violet-600 text-white font-medium rounded-xl opacity-60 cursor-not-allowed">
                    {config.button_text || 'Submit Pitch'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Field Editor */}
        {selectedFieldData && (
          <div className="w-72 bg-white border-l border-gray-200 flex flex-col flex-shrink-0 overflow-auto">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-violet-50">
              <div>
                <h3 className="font-semibold text-gray-900">Edit Field</h3>
                <p className="text-xs text-gray-500">Customize this field</p>
              </div>
              <button onClick={() => setSelectedField(null)} className="p-1.5 hover:bg-violet-100 rounded-lg">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1.5 block">Field Label</label>
                <Input
                  value={selectedFieldData.label}
                  onChange={(e) => updateField(selectedField, { label: e.target.value })}
                  placeholder="e.g., Company Name"
                  className="h-9 text-sm"
                />
              </div>
              
              {selectedFieldData.type !== FIELD_TYPES.LABEL && selectedFieldData.type !== FIELD_TYPES.FILE && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1.5 block">Placeholder Text</label>
                  <Input
                    value={selectedFieldData.placeholder || ''}
                    onChange={(e) => updateField(selectedField, { placeholder: e.target.value })}
                    placeholder="e.g., Enter your company name"
                    className="h-9 text-sm"
                  />
                </div>
              )}

              {selectedFieldData.type === FIELD_TYPES.DROPDOWN && (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1.5 block">Dropdown Options</label>
                    <Textarea
                      value={(selectedFieldData.options || []).join('\n')}
                      onChange={(e) => {
                        const lines = e.target.value.split('\n');
                        updateField(selectedField, { options: lines });
                      }}
                      onBlur={(e) => {
                        const lines = e.target.value.split('\n').filter(line => line.trim() !== '');
                        updateField(selectedField, { options: lines });
                      }}
                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                      className="text-sm min-h-[100px] font-mono text-xs"
                    />
                    <p className="text-xs text-gray-400 mt-1">One option per line</p>
                  </div>
                  
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-violet-50 rounded-lg border border-violet-100">
                    <input
                      type="checkbox"
                      checked={selectedFieldData.multiSelect || false}
                      onChange={(e) => updateField(selectedField, { multiSelect: e.target.checked })}
                      className="w-4 h-4 rounded border-violet-300 text-violet-600"
                    />
                    <div>
                      <span className="text-sm text-gray-700 font-medium">Allow multiple selections</span>
                      <p className="text-xs text-gray-400">Users can select more than one option</p>
                    </div>
                  </label>
                </div>
              )}

              {selectedFieldData.type !== FIELD_TYPES.LABEL && (
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={selectedFieldData.required || false}
                    onChange={(e) => updateField(selectedField, { required: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-violet-600"
                  />
                  <div>
                    <span className="text-sm text-gray-700 font-medium">Required field</span>
                    <p className="text-xs text-gray-400">Must be filled to submit</p>
                  </div>
                </label>
              )}

              <div className="pt-4 border-t border-gray-100">
                <button
                  onClick={() => removeField(selectedField)}
                  className="w-full py-2.5 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete this field
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
